import { GlobalStore, UserStore } from '@roc/feature-app-core';
import {
  GENERIC_ERROR_MESSAGE,
  REFINANCE
} from '@roc/feature-utils';
import { action, computed, flow, makeObservable, observable } from 'mobx';
import { QuoteService } from '../services/quoteService';
import { bridgeQuoteSteps, LoanSubType, QuoteStep } from '../utils/constants';
import { BorrowersStore } from './common/borrowersStore';
import { EntityStore } from './fixFlip/entityStore';
import { FinalDetailsStore } from './fixFlip/finalDetailsStore';
import { GroundUpPropertiesStore } from './groundUp/groundUpPropertiesStore';
import { GroundUpLoanEconomicsStore } from './groundUp/groundUpLoanEconomicsStore';

const steps = bridgeQuoteSteps;

export class GroundUpStore {
  globalStore: GlobalStore;
  userStore: UserStore;
  quoteService: QuoteService;

  capitalProviderName: string;
  savedQuote: any;
  savedLoan: any;

  entityStore: EntityStore;
  borrowersStore: BorrowersStore;
  propertiesStore: GroundUpPropertiesStore;
  finalDetailsStore: FinalDetailsStore;
  loanEconomicsStore: GroundUpLoanEconomicsStore;

  loanSubtype: LoanSubType;

  step: QuoteStep;
  lastCompletedStep: QuoteStep;

  quoteId: number;
  loanId: number;

  quoteNotFound: boolean;

  constructor(globalStore: GlobalStore, userStore: UserStore) {
    this.globalStore = globalStore;
    this.userStore = userStore;
    this.quoteService = new QuoteService();
    this.loanSubtype = LoanSubType.GROUND_UP;
    this.borrowersStore = new BorrowersStore(
      this.globalStore,
      this.loanSubtype
    );
    this.entityStore = new EntityStore(
      globalStore,
      this.borrowersStore,
      this.loanSubtype
    );
    this.propertiesStore = new GroundUpPropertiesStore(
      globalStore,
      this.loanSubtype
    );
    this.loanEconomicsStore = new GroundUpLoanEconomicsStore(
      this.globalStore,
      this
    );
    this.finalDetailsStore = new FinalDetailsStore(globalStore, userStore);

    makeObservable(this, {
      savedQuote: observable,
      savedLoan: observable,
      capitalProviderName: observable,

      loanSubtype: observable,
      step: observable,
      quoteId: observable,
      reset: action,
      goToStep: flow,
      validateCurrentStep: action,
      saveQuote: flow,
      loadQuote: flow,
      loadLoan: flow,
      getQuoteData: action,
      quoteNotFound: observable,
      isMidConstructionRefinance: computed,
      submitLoan: flow,
    });
  }

  reset() {
    this.savedQuote = null;
    this.savedLoan = null;
    this.capitalProviderName = null;

    this.step = QuoteStep.ENTITY_INFORMATION;
    this.lastCompletedStep = null;
    this.quoteId = null;
    this.loanId = null;
    this.borrowersStore.reset();
    this.propertiesStore.reset();
    this.loanEconomicsStore.reset();
    this.entityStore.reset();
    this.quoteNotFound = false;
  }

  *goToStep(step: QuoteStep) {
    if (step === QuoteStep.SUMMARY) {
      this.borrowersStore.turnOffBorrowersEditMode();
      this.propertiesStore.turnOffPropertiesEditMode();
    }

    if (steps.indexOf(step) < steps.indexOf(this.step)) {
      this.step = step;
    } else if (this.validateCurrentStep()) {
      if (this.canGoToStep(step)) {
        this.updateLastCompletedStep();
        this.step = step;
        yield this.saveQuote();
      } else {
        this.globalStore.notificationStore.showInfoNotification({
          message: 'Please fill the previous steps',
        });
      }
    }
  }

  private canGoToStep(step: QuoteStep) {
    return (
      steps.indexOf(step) <= steps.indexOf(this.lastCompletedStep) + 1 ||
      steps.indexOf(step) === steps.indexOf(this.step) + 1
    );
  }

  private updateLastCompletedStep() {
    if (steps.indexOf(this.lastCompletedStep) < steps.indexOf(this.step)) {
      this.lastCompletedStep = this.step;
    }
  }

  validateCurrentStep() {
    switch (this.step) {
      case QuoteStep.ENTITY_INFORMATION:
        return this.entityStore.validateEntity();
      case QuoteStep.BORROWER_INFORMATION:
        return this.borrowersStore.validateAllBorrowers();
      case QuoteStep.PROPERTY_INFORMATION:
        return this.propertiesStore.validateAllProperties();
      case QuoteStep.LOAN_ECONOMICS:
        return this.loanEconomicsStore.validateLoanEconomicsStep();
      case QuoteStep.LEVERAGE:
        return this.loanEconomicsStore.validateLeverageStep();
      default:
        return true;
    }
  }

  *saveQuote() {
    try {
      const isLoan = !!this.savedQuote?.loanNumber;
      const response = isLoan
        ? yield this.quoteService.saveLoan(this.getQuoteData())
        : yield this.quoteService.saveQuote(this.getQuoteData());
      this.savedQuote = response.data.data;
      this.quoteId = response.data.data.quoteId;
    } catch (e) {
      console.log(e);
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE,
      });
    }
  }

  *loadQuote(quoteNumber) {
    try {
      this.quoteNotFound = false;
      const response = yield this.quoteService.getQuoteByQuoteNumber(quoteNumber);
      const quoteData = response.data.data;
      const isSubmittedQuote = !!quoteData.loanId;
      if (!quoteData?.id || isSubmittedQuote) {
        throw new Error("Don't have access to the quote");
      }
      this.loadQuoteData(quoteData);
    } catch (e) {
      console.log(e);
      this.quoteNotFound = true;
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE,
      });
    }
  }

  *loadLoan(loanNumber) {
    try {
      this.quoteNotFound = false;
      const response = yield this.quoteService.getLoanByNumber(loanNumber);
      const quoteData = response.data.data;
      quoteData.uiState.quoteStep = QuoteStep.ENTITY_INFORMATION;
      quoteData.uiState.selectEntityOption = null;
      if (!quoteData?.id) {
        throw new Error("Don't have access to the loan");
      }
      this.loadQuoteData(quoteData);
    } catch (e) {
      console.log(e);
      this.quoteNotFound = true;
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE,
      });
    }
  }

  private loadQuoteData(quoteData) {
    this.savedQuote = quoteData;
    this.capitalProviderName = quoteData.capitalProviderName;
    this.quoteId = quoteData.quoteId;
    this.loanId = quoteData.loanId;
    this.entityStore.loadEntityData(quoteData);
    this.borrowersStore.loadBorrowers(quoteData.borrowers);
    this.propertiesStore.loadProperties(quoteData);
    this.loanEconomicsStore.loadLoanEconomics(quoteData);
    this.finalDetailsStore.loadClosingData(quoteData);
    this.step = quoteData.uiState.quoteStep;
  }

  setCapitalProviderName(name) {
    this.capitalProviderName = name;
  }

  getQuoteData() {
    return {
      ...(this.savedQuote ?? {}),
      schemaVersion: 1,
      capitalProviderName: this.capitalProviderName,
      lenderId: this.globalStore.lenderInfo.lenderId,
      loanType: this.loanSubtype,
      broker: this.loanEconomicsStore.getBroker(),
      entity: this.entityStore.getEntity(),
      borrowers: this.borrowersStore.getBorrowers(),
      properties: this.propertiesStore.getProperties(),
      closingData: this.finalDetailsStore.getClosingData(),
      loanEconomics: this.loanEconomicsStore.getLoanEconomics(),
      calculatedData: this.loanEconomicsStore.getCalculatedData(),
      assignment: this.finalDetailsStore.getAssignment(this.savedQuote),
      uiState: {
        quoteStep: this.step,
        selectEntityOption: this.entityStore.selectEntityOption,
      },
    };
  }

  *submitLoan() {
    try {
      const isLoan = !!this.savedQuote?.loanNumber;
      const quoteData = this.getQuoteData();
      if (!isLoan) {
        quoteData.quoteId = quoteData.id;
        delete quoteData.id;
        delete quoteData.version;
      }
      const response = yield this.quoteService.saveLoan(quoteData);
      this.savedLoan = response.data.data;
      this.step = QuoteStep.SUCCESS;
    } catch (e) {
      console.log(e);
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE,
      });
    }
  }

  get isMidConstructionRefinance() {
    return this.propertiesStore.getProperties().some(prop => {
      const sunkCost = prop.renovation?.capitalImprovements || 0;
      return prop.loanPurpose === REFINANCE && sunkCost > 0;
    });
  }
}